import { registerCarePlan } from "../slices/carePlanSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch a CarePlan by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchCarePlan = async (
    carePlanId,
    fhirClient,
    store,
    { forceFetch = false } = {}
) => {
    console.log("fetching CarePlan by id");

    if (!carePlanId || !fhirClient) return null;

    // 🧠 Check Redux cache
    const cached = store.getState().carePlans?.[carePlanId];
    if (cached && !forceFetch) {
        console.log(`🧠 CarePlan ${carePlanId} served from cache`);
        return cached;
    }

    try {
        console.log(`🌐 Fetching CarePlan/${carePlanId} from FHIR`);
        const fetched = await fhirClient.request(`CarePlan/${carePlanId}`);

        if (fetched?.resourceType !== "CarePlan") {
            console.warn("⚠️ Unexpected CarePlan response:", fetched);
            return null;
        }

        store.dispatch(registerCarePlan(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch CarePlan:", err);

        const status = err?.response?.status || err?.status || 500;

        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "CarePlan fetch failed",
                status,
                resourceId: `CarePlan/${carePlanId}`
            },
            description: `Failed to retrieve CarePlan/${carePlanId} from FHIR server`,
            store
        });

        // Standardized error return
        return { error: status };
    }
};

/**
 * Fetch a CarePlan given a FHIR reference string
 * e.g. "CarePlan/123" or "CarePlan/123/_history/4"
 */
export const fetchCarePlanByReference = async (
    ref,
    fhirClient,
    store,
    options = {}
) => {
    console.log("fetching CarePlan by Reference....................");

    if (!ref || typeof ref !== "string") {
        console.warn("⚠️ Invalid CarePlan reference (not a string):", ref);
        return null;
    }

    if (!ref.startsWith("CarePlan/")) {
        console.warn("⚠️ Reference is not a CarePlan:", ref);
        return null;
    }

    // Safely extract ID (handles versioned refs)
    const parts = ref.split("/");
    const carePlanId = parts[1];

    if (!carePlanId || carePlanId === "undefined" || carePlanId === "null") {
        console.warn("⚠️ Could not extract valid CarePlan ID from reference:", ref);
        return null;
    }

    return fetchCarePlan(carePlanId, fhirClient, store, options);
};
